import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Eye, Pencil, ShieldAlert, Loader2, ChevronDown, ChevronRight, Lock, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/App';

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');

const Stat = ({ icon: Icon, n, label, tone }) => (
  <span className={`inline-flex items-center gap-1 ${tone}`} title={label}>
    <Icon className="w-3.5 h-3.5" /> {n}
  </span>
);

/** The plain-sentence table for one session. */
const SessionEvents = ({ grantId }) => {
  const [rows, setRows] = useState(null);
  const [showBackground, setShowBackground] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/account-access/sessions/${grantId}`)
      .then((r) => setRows(r.data.events || []))
      .catch(() => setRows([]));
  }, [grantId]);

  if (rows === null) {
    return <p className="text-xs text-gray-400 py-3 flex items-center gap-2">
      <Loader2 className="w-3 h-3 animate-spin" /> Loading the record…
    </p>;
  }
  if (!rows.length) {
    return <p className="text-xs text-gray-400 py-3">Nothing was recorded for this session.</p>;
  }

  const visible = showBackground ? rows : rows.filter((r) => !r.background);
  const hidden = rows.length - rows.filter((r) => !r.background).length;

  return (
    <div className="py-2" data-testid={`access-events-${grantId}`}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-400 text-left border-b border-gray-700">
            <th className="py-1.5 pr-3 font-medium">What happened</th>
            <th className="py-1.5 pr-3 font-medium">Area</th>
            <th className="py-1.5 pr-3 font-medium">When</th>
            <th className="py-1.5 pr-3 font-medium">Times</th>
            <th className="py-1.5 font-medium">From</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((e) => (
            <tr key={e.id} className="border-b border-gray-800/60 align-top"
                data-testid={`access-event-row-${e.id}`}>
              <td className="py-1.5 pr-3">
                <span className={
                  e.event === 'refused' ? 'text-amber-300'
                    : e.event === 'action' ? 'text-emerald-300' : 'text-gray-200'
                }>
                  {e.what}
                </span>
                {e.private && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-rose-300">
                    <Lock className="w-2.5 h-2.5" /> private
                  </span>
                )}
                {e.diagnostic && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-sky-300">
                    <Download className="w-2.5 h-2.5" /> data taken out
                  </span>
                )}
                <span className="block text-[10px] text-gray-600 font-mono">
                  {e.method} {e.path}
                </span>
              </td>
              <td className="py-1.5 pr-3 text-gray-400">{e.area}</td>
              <td className="py-1.5 pr-3 text-gray-400 whitespace-nowrap">{when(e.at)}</td>
              <td className="py-1.5 pr-3 text-gray-400">{e.times}</td>
              <td className="py-1.5 text-gray-500 font-mono text-[10px]">{e.ip || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hidden > 0 && (
        <button
          onClick={() => setShowBackground((v) => !v)}
          className="mt-2 text-[11px] text-gray-500 hover:text-gray-300 transition-colors"
          data-testid={`access-background-toggle-${grantId}`}
        >
          {showBackground ? 'Hide' : `Show ${hidden} background`} page-loading entries
        </button>
      )}
    </div>
  );
};

/**
 * Admin → Account Access: who went into whose account, with permission, and what they did.
 *
 * Built for the awkward conversation rather than for curiosity — a member saying something was
 * looked at when it wasn't, or an employee admin needing to be accountable for a support visit.
 */
export const AdminAccountAccess = () => {
  const [params] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [scope, setScope] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState('');
  const memberId = params.get('member');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/account-access/sessions`, {
        params: memberId ? { member_id: memberId } : {},
      });
      setSessions(r.data.sessions || []);
      setScope(r.data.scope || '');
    } finally { setLoading(false); }
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  const shown = sessions.filter((s) => {
    if (!filter.trim()) return true;
    const hay = `${s.member} ${s.member_email} ${s.admin} ${s.reason}`.toLowerCase();
    return hay.includes(filter.trim().toLowerCase());
  });

  return (
    <div className="space-y-4" data-testid="admin-account-access">
      <div>
        <h2 className="text-lg font-semibold text-white">Account Access</h2>
        <p className="text-sm text-gray-400 mt-1 max-w-3xl">
          Every time an admin was let into a member&apos;s account, with the member&apos;s
          permission on record. Each session lists everywhere that was looked at and everything
          that was changed, in plain words. Showing {scope || '…'}.
        </p>
      </div>

      <Input
        placeholder="Search by member, admin or reason"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="max-w-sm text-sm"
        data-testid="access-log-search"
      />

      {loading ? (
        <p className="text-sm text-gray-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading the access record…
        </p>
      ) : !shown.length ? (
        <p className="text-sm text-gray-400" data-testid="access-log-empty">
          No account has been accessed this way yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-800/60">
              <tr className="text-left text-gray-300">
                <th className="p-3 font-medium w-6"></th>
                <th className="p-3 font-medium">Member who authorised</th>
                <th className="p-3 font-medium">Admin</th>
                <th className="p-3 font-medium">Reason given</th>
                <th className="p-3 font-medium">Asked / authorised</th>
                <th className="p-3 font-medium">Session</th>
                <th className="p-3 font-medium">Activity</th>
                <th className="p-3 font-medium">Access revoked</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((s) => (
                <React.Fragment key={s.id}>
                  <tr
                    className="border-t border-gray-800 hover:bg-gray-800/40 transition-colors cursor-pointer"
                    onClick={() => setOpen(open === s.id ? null : s.id)}
                    data-testid={`access-session-row-${s.id}`}
                  >
                    <td className="p-3 text-gray-500">
                      {open === s.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="p-3">
                      <span className="text-white">{s.member}</span>
                      <span className="block text-xs text-gray-500">{s.member_email}</span>
                    </td>
                    <td className="p-3 text-gray-300">{s.admin}</td>
                    <td className="p-3 text-gray-300 max-w-xs">{s.reason}</td>
                    <td className="p-3 text-xs text-gray-400 whitespace-nowrap">
                      {when(s.requested_at)}
                      <span className="block text-gray-500">{s.authorised_at ? when(s.authorised_at) : 'not authorised'}</span>
                    </td>
                    <td className="p-3 text-xs text-gray-400 whitespace-nowrap">
                      {s.started_at ? when(s.started_at) : 'never entered'}
                      <span className="block text-gray-500">{s.ended_at ? when(s.ended_at) : '—'}</span>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <span className="inline-flex items-center gap-3">
                        <Stat icon={Eye} n={s.views} label="pages looked at" tone="text-gray-300" />
                        <Stat icon={Pencil} n={s.changes} label="changes made" tone="text-emerald-300" />
                        {s.refused > 0 && (
                          <Stat icon={ShieldAlert} n={s.refused} label="attempts refused" tone="text-amber-300" />
                        )}
                      </span>
                      {s.diagnostics_collected && (
                        <span className="block text-[10px] text-sky-300 mt-0.5">data taken out of the account</span>
                      )}
                    </td>
                    <td className={`p-3 text-xs ${s.status === 'approved' ? 'text-amber-300' : 'text-gray-300'}`}>
                      {s.access_revoked}
                      <span className="block text-gray-500">{s.outcome}</span>
                    </td>
                  </tr>
                  {open === s.id && (
                    <tr className="bg-gray-900/70">
                      <td colSpan={8} className="px-6">
                        <SessionEvents grantId={s.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-500 max-w-3xl">
        Kept for as long as the member&apos;s account exists, and through the seven years an
        archived account is held, like everything else in the Archive. &quot;Private&quot; marks
        an area the member would consider personal — messages, journal, intake answers.
        Page-loading entries are the browser fetching notifications and counts on its own, not
        somebody opening a page.
      </p>
    </div>
  );
};

export default AdminAccountAccess;
